// src/pages/admin/IntentionPeriods.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, AlertCircle, Archive, Play, Pause, Trash2, Edit2, X } from 'lucide-react';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { IntentionPeriod, IntentionPeriodFormData, IntentionPeriodStats } from '../../types/intentionPeriods';
import EmptyState from '../../components/ui/EmptyState';
import { useConfirm } from '../../contexts/ConfirmContext';

const IntentionPeriods: React.FC = () => {
  const confirm = useConfirm();
  const [periods, setPeriods] = useState<IntentionPeriod[]>([]);
  const [periodStats, setPeriodStats] = useState<Record<string, IntentionPeriodStats>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<IntentionPeriod | null>(null);
  const [formData, setFormData] = useState<IntentionPeriodFormData>({
    name: '',
    academicYear: new Date().getFullYear() + 1,
    opensAt: '',
    dueDate: '',
    closesAt: '',
    status: 'draft',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const data = await intentionPeriodsService.getAllPeriods();
      setPeriods(data);

      // Load stats for each period
      const stats: Record<string, IntentionPeriodStats> = {};
      for (const period of data) {
        const periodStats = await intentionPeriodsService.getPeriodStats(period.id);
        if (periodStats) {
          stats[period.id] = periodStats;
        }
      }
      setPeriodStats(stats);
    } catch (err) {
      console.error('Error loading periods:', err);
      setError('Failed to load intention periods');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePeriod = () => {
    setEditingPeriod(null);
    setFormData({
      name: `${new Date().getFullYear() + 1} Teaching Intentions`,
      academicYear: new Date().getFullYear() + 1,
      opensAt: '',
      dueDate: '',
      closesAt: '',
      status: 'draft',
      description: 'Please submit your teaching intentions for the upcoming academic year.'
    });
    setShowModal(true);
  };

  const handleEditPeriod = (period: IntentionPeriod) => {
    setEditingPeriod(period);
    setFormData({
      name: period.name,
      academicYear: period.academicYear,
      opensAt: period.opensAt.toISOString().slice(0, 16),
      dueDate: period.dueDate ? period.dueDate.toISOString().slice(0, 16) : '',
      closesAt: period.closesAt.toISOString().slice(0, 16),
      status: period.status,
      description: period.description || ''
    });
    setShowModal(true);
  };

  const handleSavePeriod = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.name || !formData.opensAt || !formData.closesAt) {
        setError('Please fill in all required fields');
        return;
      }

      if (new Date(formData.closesAt) <= new Date(formData.opensAt)) {
        setError('Close date must be after open date');
        return;
      }

      // v1.6.1: due date is optional, but if set it must fall between opens and closes.
      if (formData.dueDate) {
        const due = new Date(formData.dueDate);
        if (due < new Date(formData.opensAt)) {
          setError('Due date cannot be earlier than the open date');
          return;
        }
        if (due > new Date(formData.closesAt)) {
          setError('Due date cannot be later than the close date');
          return;
        }
      }

      if (editingPeriod) {
        await intentionPeriodsService.updatePeriod(editingPeriod.id, formData);
      } else {
        // Don't pass created_by - the auth user ID doesn't match staff table UUIDs
        await intentionPeriodsService.createPeriod(formData);
      }

      setShowModal(false);
      await loadPeriods();
    } catch (err: any) {
      console.error('Error saving period:', err);
      setError(err.message || 'Failed to save period');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (period: IntentionPeriod, newStatus: 'open' | 'closed' | 'archived') => {
    try {
      if (newStatus === 'open') {
        await intentionPeriodsService.openPeriod(period.id);
      } else if (newStatus === 'closed') {
        await intentionPeriodsService.closePeriod(period.id);
      } else {
        await intentionPeriodsService.archivePeriod(period.id);
      }
      await loadPeriods();
    } catch (err: any) {
      console.error('Error changing status:', err);
      setError(err.message || 'Failed to change period status');
    }
  };

  const handleDeletePeriod = async (period: IntentionPeriod) => {
    if (!(await confirm({
      title: 'Delete period?',
      message: `Are you sure you want to delete "${period.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) {
      return;
    }

    try {
      await intentionPeriodsService.deletePeriod(period.id);
      await loadPeriods();
    } catch (err: any) {
      console.error('Error deleting period:', err);
      setError(err.message || 'Failed to delete period');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      draft: { bg: 'bg-ink-100', text: 'text-ink-900', icon: <Edit2 className="w-3 h-3" /> },
      open: { bg: 'bg-ok-bg', text: 'text-ok-fg', icon: <Play className="w-3 h-3" /> },
      closed: { bg: 'bg-warn-bg', text: 'text-warn-fg', icon: <Pause className="w-3 h-3" /> },
      archived: { bg: 'bg-ink-100', text: 'text-ink-500', icon: <Archive className="w-3 h-3" /> }
    };

    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-ink-200 rounded w-1/4"></div>
          <div className="h-64 bg-ink-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Intention Submission Periods</h1>
          <p className="text-ink-500 mt-1">
            Manage submission windows for staff teaching intentions
          </p>
        </div>
        <button
          onClick={handleCreatePeriod}
          className="flex items-center gap-2 px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Period
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-danger-bg border border-danger-border rounded-lg text-danger-fg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Periods List */}
      <div className="space-y-4">
        {periods.length === 0 ? (
          <div className="bg-white rounded-lg border border-ink-200">
            <EmptyState
              icon={<Calendar className="h-6 w-6" />}
              title="No Submission Periods"
              description="Create your first submission period to allow staff to submit their teaching intentions."
              action={<button
                onClick={handleCreatePeriod}
                className="inline-flex items-center gap-2 px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700"
              >
                <Plus className="w-4 h-4" />
                Create Period
              </button>}
            />
          </div>
        ) : (
          periods.map((period) => {
            const stats = periodStats[period.id];
            const isCurrentlyOpen = intentionPeriodsService.isPeriodOpen(period);
            const timeRemaining = intentionPeriodsService.getTimeUntilClose(period);

            return (
              <div
                key={period.id}
                className="bg-white rounded-lg border border-ink-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-card-title text-ink-900">{period.name}</h3>
                      {getStatusBadge(period.status)}
                      {isCurrentlyOpen && timeRemaining && (
                        <span className="font-mono tabular-nums text-xs text-ok-fg bg-ok-bg px-2 py-1 rounded">
                          {timeRemaining.days}d {timeRemaining.hours}h remaining
                        </span>
                      )}
                    </div>

                    <div className="flex items-center flex-wrap gap-x-6 gap-y-1 text-sm text-ink-500 mb-4">
                      <span className="flex items-center gap-1 font-mono tabular-nums">
                        <Calendar className="w-4 h-4" />
                        {period.academicYear}
                      </span>
                      <span className="flex items-center gap-1 font-mono tabular-nums">
                        <Clock className="w-4 h-4" />
                        {formatDate(period.opensAt)} - {formatDate(period.closesAt)}
                      </span>
                      {period.dueDate && (
                        <span className="flex items-center gap-1 text-warn-fg font-mono tabular-nums" title="Submissions after this are marked late">
                          <AlertCircle className="w-4 h-4" />
                          Due {formatDate(period.dueDate)}
                        </span>
                      )}
                    </div>

                    {period.description && (
                      <p className="text-sm text-ink-500 mb-4">{period.description}</p>
                    )}

                    {/* Stats */}
                    {stats && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-ink-100">
                        <div>
                          <p className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.totalSubmissions}</p>
                          <p className="text-xs text-ink-500">Total Submissions</p>
                        </div>
                        <div>
                          <p className="font-mono tabular-nums text-2xl font-bold text-warn-fg">{stats.pendingReviewCount}</p>
                          <p className="text-xs text-ink-500">Pending Review</p>
                        </div>
                        <div>
                          <p className="font-mono tabular-nums text-2xl font-bold text-ok-fg">{stats.hrApprovedCount}</p>
                          <p className="text-xs text-ink-500">Approved</p>
                        </div>
                        <div>
                          <p className="font-mono tabular-nums text-2xl font-bold text-danger-fg">{stats.hrFlaggedCount}</p>
                          <p className="text-xs text-ink-500">Flagged</p>
                        </div>
                        <div>
                          <p className="font-mono tabular-nums text-2xl font-bold text-navy-600">{stats.submissionRate}%</p>
                          <p className="text-xs text-ink-500">Submission Rate</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEditPeriod(period)}
                      className="p-2 text-ink-400 hover:text-ink-500 rounded-lg hover:bg-ink-100"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>

                    {period.status === 'draft' && (
                      <button
                        onClick={() => handleStatusChange(period, 'open')}
                        className="p-2 text-ok-fg hover:text-ok-fg rounded-lg hover:bg-ok-bg"
                        title="Open Period"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}

                    {period.status === 'open' && (
                      <button
                        onClick={() => handleStatusChange(period, 'closed')}
                        className="p-2 text-warn-fg hover:text-warn-fg rounded-lg hover:bg-warn-bg"
                        title="Close Period"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    )}

                    {period.status === 'closed' && (
                      <button
                        onClick={() => handleStatusChange(period, 'archived')}
                        className="p-2 text-ink-400 hover:text-ink-500 rounded-lg hover:bg-ink-100"
                        title="Archive Period"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}

                    {(period.status === 'draft' || period.status === 'archived') && (
                      <button
                        onClick={() => handleDeletePeriod(period)}
                        className="p-2 text-danger-solid hover:text-danger-fg rounded-lg hover:bg-danger-bg"
                        title="Delete Period"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-card-title text-ink-900">
                {editingPeriod ? 'Edit Period' : 'Create Submission Period'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 text-ink-400 hover:text-ink-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Period Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  placeholder="e.g., 2027 Teaching Intentions"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Academic Year *
                </label>
                <input
                  type="number"
                  value={formData.academicYear}
                  onChange={(e) => setFormData({ ...formData, academicYear: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  min={2024}
                  max={2100}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Opens At *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.opensAt}
                    onChange={(e) => setFormData({ ...formData, opensAt: e.target.value })}
                    className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  />
                  <p className="mt-1 text-xs text-ink-500">Teachers can start submitting from this time.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Closes At *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.closesAt}
                    onChange={(e) => setFormData({ ...formData, closesAt: e.target.value })}
                    className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  />
                  <p className="mt-1 text-xs text-ink-500">Hard close — form becomes read-only after this.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Due Date <span className="text-ink-400 font-normal">(optional)</span>
                </label>
                <input
                  type="datetime-local"
                  value={formData.dueDate || ''}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                />
                <p className="mt-1 text-xs text-ink-500">
                  Public deadline shown to teachers. Submissions received after this are marked <strong>late</strong>, but the form remains editable until the close date.
                  Must fall between Opens and Closes if set.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                >
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Instructions for Staff
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                  placeholder="Enter instructions or notes for staff..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t bg-ink-50 rounded-b-lg">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-ink-700 hover:text-ink-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePeriod}
                disabled={saving}
                className="px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingPeriod ? 'Save Changes' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntentionPeriods;
